import { memo, useCallback, useRef, useState } from 'react';
import {
  EMPTY_INPUT_STATE,
  mergeInputState,
} from './inputState';
import {
  getJoystickVector,
  resetJoystickVector,
} from './joystickMath';

const DEFAULT_ACTIONS = [
  { control: 'primary', label: 'Primary action', text: 'A' },
  { control: 'boost', label: 'Boost', text: 'B' },
];

function actionStateFor(control, pressed) {
  switch (control) {
    case 'primary':
      return { primary: pressed, confirm: pressed };
    case 'secondary':
      return { secondary: pressed };
    case 'melee':
      return { melee: pressed };
    case 'boost':
      return { boost: pressed, secondary: pressed };
    case 'jump':
      return { jump: pressed };
    case 'confirm':
      return { confirm: pressed };
    case 'cancel':
      return { cancel: pressed };
    case 'pause':
      return { pause: pressed };
    default:
      return {};
  }
}

function getActionAimVector(event, element) {
  const rect = element.getBoundingClientRect();
  const origin = {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };

  const joystick = getJoystickVector(origin, {
    x: event.clientX,
    y: event.clientY,
  });

  return {
    aimX: joystick.x,
    aimY: joystick.y,
  };
}

function GameController({
  actions = DEFAULT_ACTIONS,
  ariaLabel = 'Game controller',
  className = '',
  movementLabel = 'Move',
  onInputChange,
}) {
  const movementPointerIdRef = useRef(null);
  const originRef = useRef(null);
  const inputRef = useRef(EMPTY_INPUT_STATE);
  const actionPointersRef = useRef(new Map());
  const [joystick, setJoystick] = useState(resetJoystickVector);
  const [origin, setOrigin] = useState(null);
  const [moveZoneSide, setMoveZoneSide] = useState(null);

  const emitInput = useCallback((partialInput) => {
    const nextInput = mergeInputState(inputRef.current, partialInput);

    inputRef.current = nextInput;
    onInputChange?.(nextInput);
  }, [onInputChange]);

  const resetMovement = useCallback(() => {
    movementPointerIdRef.current = null;
    originRef.current = null;
    setOrigin(null);
    setMoveZoneSide(null);
    setJoystick(resetJoystickVector());
    emitInput({ moveX: 0, moveY: 0 });
  }, [emitInput]);

  const updateMovement = useCallback((event, nextOrigin = originRef.current) => {
    if (!nextOrigin) {
      return;
    }

    const nextJoystick = getJoystickVector(nextOrigin, {
      x: event.clientX,
      y: event.clientY,
    });

    setJoystick(nextJoystick);
    emitInput({
      moveX: nextJoystick.x,
      moveY: nextJoystick.y,
    });
  }, [emitInput]);

  const releaseActionPointer = useCallback((pointerId) => {
    const action = actionPointersRef.current.get(pointerId);

    if (!action) {
      return;
    }

    actionPointersRef.current.delete(pointerId);
    emitInput({
      ...actionStateFor(action.control, false),
      ...(action.aim ? { aimX: 0, aimY: 0 } : {}),
    });
  }, [emitInput]);

  return (
    <div
      className={`game-controller${moveZoneSide ? ` game-controller-move-${moveZoneSide}` : ''} ${className}`.trim()}
      aria-label={ariaLabel}
    >
      <div
        className={`game-controller-move-zone${origin ? ' game-controller-move-zone-active' : ''}${moveZoneSide ? ` game-controller-move-zone-${moveZoneSide}` : ''}`}
        role="slider"
        tabIndex="0"
        aria-label={movementLabel}
        aria-valuemin="-100"
        aria-valuemax="100"
        aria-valuenow={Math.round(Math.max(Math.abs(joystick.x), Math.abs(joystick.y)) * 100)}
        aria-valuetext={`X ${Math.round(joystick.x * 100)}, Y ${Math.round(joystick.y * 100)}`}
        onPointerDown={(event) => {
          if (movementPointerIdRef.current !== null) {
            return;
          }

          const nextOrigin = { x: event.clientX, y: event.clientY };
          const side = event.clientX < window.innerWidth / 2 ? 'left' : 'right';

          event.currentTarget.setPointerCapture?.(event.pointerId);
          movementPointerIdRef.current = event.pointerId;
          originRef.current = nextOrigin;
          setOrigin(nextOrigin);
          setMoveZoneSide(side);
          updateMovement(event, nextOrigin);
        }}
        onPointerMove={(event) => {
          if (event.pointerId === movementPointerIdRef.current) {
            updateMovement(event);
          }
        }}
        onPointerUp={(event) => {
          if (event.pointerId === movementPointerIdRef.current) {
            resetMovement();
          }
        }}
        onPointerCancel={(event) => {
          if (event.pointerId === movementPointerIdRef.current) {
            resetMovement();
          }
        }}
      >
        {origin && (
          <span
            className="game-controller-joystick"
            aria-hidden="true"
            style={{
              '--origin-x': `${origin.x}px`,
              '--origin-y': `${origin.y}px`,
              '--thumb-x': `${joystick.thumbX}px`,
              '--thumb-y': `${joystick.thumbY}px`,
            }}
          >
            <span className="game-controller-joystick-ring" />
            <span className="game-controller-joystick-thumb">
              <span />
            </span>
          </span>
        )}
      </div>

      {actions.length > 0 && (
        <div className="game-controller-actions" aria-label="Action controls">
          {actions.map((action) => (
            <button
              className={[
                'game-controller-action',
                `game-controller-action-${action.control}`,
                action.className ?? '',
              ].filter(Boolean).join(' ')}
              type="button"
              aria-label={action.label}
              key={action.control}
              onPointerDown={(event) => {
                event.currentTarget.setPointerCapture?.(event.pointerId);
                actionPointersRef.current.set(event.pointerId, {
                  control: action.control,
                  aim: Boolean(action.aim),
                });
                emitInput({
                  ...actionStateFor(action.control, true),
                  ...(action.aim ? getActionAimVector(event, event.currentTarget) : {}),
                });
              }}
              onPointerMove={(event) => {
                const activeAction = actionPointersRef.current.get(event.pointerId);

                if (!activeAction?.aim) {
                  return;
                }

                emitInput({
                  ...actionStateFor(activeAction.control, true),
                  ...getActionAimVector(event, event.currentTarget),
                });
              }}
              onPointerUp={(event) => releaseActionPointer(event.pointerId)}
              onPointerCancel={(event) => releaseActionPointer(event.pointerId)}
              onPointerLeave={(event) => releaseActionPointer(event.pointerId)}
            >
              <span aria-hidden="true">{action.text}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default memo(GameController);
